/**
 * ROLES & ACCESS (design_handoff_pro_setup README §5): who can manage a Pro page. The owner row
 * wears the cyan OWNER pill and can't be removed — ownership only moves by a transfer. Manager
 * rows carry an ADMIN / MEMBER segmented control and a two-tap REMOVE. Add = search Portal Pages
 * + ADMIN / MEMBER picker. The footer is the three permission tiles. Every change writes through
 * `/pro/{slug}/members` at once.
 *
 * Owner and admins see and use this; a member sees nothing (the dashboard doesn't mount it).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { proService, searchService } from '../../services';
import { INK, MUTED, SUBTLE, INSET, CHIP, CYAN, RED, RULE, PillButton, TagPill, Face, useArmed } from '../club/clubUi';
import { Card, CardHead, pillInput } from './setup/setupUi';

export const PERMS = [
  ['OWNER', 'Everything, plus payouts, billing, closing the page and transferring it.'],
  ['ADMIN', 'Edit every part of the page, offers, contacts and sends. Add and remove members and admins. Can request a transfer.'],
  ['MEMBER', 'Scan offers at the counter and see today’s claims. Can’t edit the page.'],
];

export default function RolesAccessPanel({ slug, currentUserId, phone = false, say, onChanged, members: membersProp = null }) {
  const [members, setMembers] = useState(membersProp || []);
  const [loading, setLoading] = useState(!membersProp);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [as, setAs] = useState('admin');
  const [error, setError] = useState('');
  const { armed, confirm, disarm } = useArmed(5000);

  const load = useCallback(async () => {
    if (!slug) return;
    const r = await proService.listMembers(slug);
    if (r?.success) setMembers(r.data || []);
    setLoading(false);
  }, [slug]);
  useEffect(() => { if (membersProp) { setMembers(membersProp); setLoading(false); } else load(); }, [load, membersProp]);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setHits([]); return undefined; }
    const t = setTimeout(async () => {
      const r = await searchService.searchPeople({ q: term, take: 6 });
      const taken = new Set(members.map((m) => m.userId));
      setHits(r?.success ? (r.data?.items || []).filter((p) => !taken.has(p.id)).slice(0, 5) : []);
    }, 200);
    return () => clearTimeout(t);
  }, [q, members]);

  const apply = (r, toast) => {
    if (!r?.success) { setError(r?.error || 'Could not change that.'); say?.((r?.error || 'COULD NOT CHANGE THAT').toUpperCase()); return; }
    setError('');
    setMembers(r.data || []);
    onChanged?.(r.data || []);
    if (toast) say?.(toast);
  };
  const add = async (p) => { setQ(''); setHits([]); apply(await proService.addMember(slug, p.id, as), `${(p.title || '').toUpperCase()} ADDED AS ${as.toUpperCase()}`); };
  const setRole = async (m, role) => { if (m.role === role) return; apply(await proService.updateMemberRole(slug, m.userId, role), null); };
  const remove = async (m) => { if (!confirm(`rm-${m.userId}`)) return; apply(await proService.removeMember(slug, m.userId), `${(m.displayName || '').toUpperCase()} REMOVED`); };

  const owner = members.find((m) => m.isOwner);
  const rows = members.filter((m) => !m.isOwner);

  return (
    <Card phone={phone} style={{ gap: 12 }} data-testid="roles-card">
      <CardHead title="ROLES & ACCESS" right={`${members.length} MANAGING`} />
      <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>Who can manage this page. Admins can change anything on the page. Members can scan offers. The owner can’t be removed — ownership only moves by a transfer.</span>
      {loading && <span style={{ fontSize: 11, letterSpacing: '.12em', color: SUBTLE }}>LOADING…</span>}
      {owner && (
        <div className="flex items-center" style={{ gap: 12, padding: '8px 14px 8px 8px', borderRadius: 22, background: INSET }} data-testid="owner-row">
          <Face src={owner.avatarUrl} name={owner.displayName} size={40} />
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{owner.displayName}{owner.userId === currentUserId ? ' (you)' : ''}</span>{owner.handle && <span style={{ fontSize: 10, color: MUTED }}>@{owner.handle}</span>}</span>
          <TagPill bg={CYAN} fg={INK} size={9} padding="5px 11px">OWNER</TagPill>
        </div>
      )}
      {rows.map((m) => {
        const isArmed = armed === `rm-${m.userId}`;
        return (
          <div key={m.userId} className="flex flex-wrap items-center" style={{ gap: 10, padding: 8, borderRadius: 22, background: INSET }} data-testid={`member-${m.userId}`}>
            <Face src={m.avatarUrl} name={m.displayName} size={40} />
            <span className="flex min-w-0 flex-col" style={{ flex: '1 1 140px', gap: 2 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{m.displayName}{m.userId === currentUserId ? ' (you)' : ''}</span>{m.handle && <span style={{ fontSize: 10, color: MUTED }}>@{m.handle}</span>}</span>
            <span role="radiogroup" aria-label={`Role for ${m.displayName}`} className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: '#fff' }}>
              {[['admin', 'ADMIN'], ['member', 'MEMBER']].map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={m.role === k} onClick={() => setRole(m, k)} className="font-mono" style={{ height: 32, padding: '0 12px', border: 'none', borderRadius: 999, background: m.role === k ? INK : 'transparent', color: m.role === k ? '#fff' : INK, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>{l}</button>)}
            </span>
            <button type="button" onClick={() => remove(m)} aria-label={`Remove ${m.displayName}`} className="font-mono" style={{ height: 32, padding: '0 12px', border: 'none', borderRadius: 999, background: isArmed ? RED : '#fff', color: isArmed ? '#fff' : RED, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>{isArmed ? 'REMOVE?' : 'REMOVE'}</button>
            {isArmed && <PillButton variant="ghost" size={32} fontSize={9} onClick={disarm}>KEEP</PillButton>}
          </div>
        );
      })}
      <div className="relative flex" style={{ gap: 8 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add someone — search Portal Pages" aria-label="Add someone" className="pr-pill-input font-mono" style={{ ...pillInput(46, { fontSize: 13 }), flex: 1, minWidth: 0 }} data-testid="add-member-search" />
        <span role="radiogroup" aria-label="Add as" className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: CHIP }}>
          {[['admin', 'ADMIN'], ['member', 'MEMBER']].map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={as === k} onClick={() => setAs(k)} className="font-mono" style={{ height: 40, padding: '0 12px', border: 'none', borderRadius: 999, background: as === k ? INK : 'transparent', color: as === k ? '#fff' : INK, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>{l}</button>)}
        </span>
        {hits.length > 0 && (
          <div className="absolute left-0 right-0 z-20" style={{ top: '100%', marginTop: 6, padding: 6, borderRadius: 18, background: '#fff', boxShadow: 'inset 0 0 0 2px #000' }}>
            {hits.map((p) => (
              <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); add(p); }} className="flex w-full items-center text-left font-mono hover:bg-gray-100" style={{ gap: 10, padding: '8px 10px', border: 'none', borderRadius: 12, background: 'transparent', cursor: 'pointer' }} data-testid={`add-member-${p.id}`}>
                <Face src={p.imageUrl} name={p.title} size={28} />
                <span style={{ fontSize: 12, fontWeight: 700 }}>{p.title}</span>
                {p.meta && <span className="truncate" style={{ fontSize: 10, color: SUBTLE }}>{p.meta}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span style={{ fontSize: 11, color: RED }}>{error}</span>}
      <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(3,minmax(0,1fr))', gap: 6, paddingTop: 12, borderTop: RULE }}>
        {PERMS.map(([r, d]) => <span key={r} className="flex flex-col" style={{ gap: 6, padding: '12px 14px', borderRadius: 18, background: INSET }}><span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.14em' }}>{r}</span><span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{d}</span></span>)}
      </div>
    </Card>
  );
}
